import { useQuery } from "@apollo/client";
import { ArrowLongRightIcon, ClockIcon, PlayIcon, StarIcon } from "@heroicons/react/24/outline";
import LionIcon from "components/icons/LionIcon";
import FrequentlyAskedQuestions from "components/irpf/FrequentlyAskedQuestions";
import NavbarLayout from "components/layouts/NavbarLayout";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import getYoutubeEmbedUrl from "components/utils/getEmbedUrl";
import getYear from "date-fns/getYear";
import MostRepliedPostsQuery from "lib/graphql/queries/engagement/MostRepliedPostsQuery";
import type {
  IMostRepliedPostsQuery,
  IMostRepliedPostsQueryVariables,
} from "lib/graphql/queries/engagement/__generated__/MostRepliedPostsQuery.generated";
import YoutubePlaylistByIdQuery from "lib/queries/YoutubePlaylistByIdQuery";
import type {
  IYoutubePlaylistByIdQuery,
  IYoutubePlaylistByIdQueryVariables,
} from "lib/queries/__generated__/YoutubePlaylistByIdQuery.generated";
import formatTimeFromSeconds from "lib/utils/formatTimeFromSeconds";
import getBaseUrl from "lib/utils/getBaseUrl";
import Link from "next/link";
import { jsonLdScriptProps } from "react-schemaorg";
import type { VideoObject } from "schema-dts";

export default function IRPFPage() {
  const year = getYear(new Date());

  const { data, loading } = useQuery<IYoutubePlaylistByIdQuery, IYoutubePlaylistByIdQueryVariables>(
    YoutubePlaylistByIdQuery,
    {
      variables: {
        playlistId: "PLHAwhClPYLzSZcBfYR38sXZcuvyBfOvR6",
      },
    },
  );

  const { data: dataPostsMostReplied, loading: loadingPostsMostReplied } = useQuery<
    IMostRepliedPostsQuery,
    IMostRepliedPostsQueryVariables
  >(MostRepliedPostsQuery);

  const totalOfDurationInSeconds = data?.youtubePlaylistById?.reduce(
    (total, item) => total + item.video.durationInSeconds,
    0,
  );

  const pageTitle = `IRPF ${year} | Fundamentei`;

  return (
    <NavbarLayout>
      <FundamenteiMetadata
        title={pageTitle}
        description="Se você investe na Bolsa de Valores, é importante saber como declarar corretamente seus investimentos no Imposto de Renda (IRPF), veja aqui o passo a passo..."
      />

      <div
        className={classNames(
          "mx-auto grid w-full max-w-7xl grid-cols-4 gap-x-4 gap-y-6 px-4 py-6",
          "xl:grid-cols-12 xl:gap-x-9 xl:gap-y-10 xl:py-14 xl:px-0",
        )}
      >
        <div
          className={classNames(
            "col-span-4 flex flex-col items-center space-x-3 space-y-3",
            "md:col-span-4 md:flex-row md:space-y-0 xl:col-span-12 xl:space-x-6",
          )}
        >
          <LionIcon className="h-12 w-9 xl:h-16 xl:w-12" />

          <div className="flex flex-col items-center space-y-1 font-aeonik md:items-start ">
            <h1 className="m-0 text-xl font-bold text-gray-800 xl:text-3xl">Imposto de Renda {year}</h1>
            <span className="text-xs font-normal italic text-gray-500 xl:text-base">
              Todas as suas dúvidas—respondidas, em um só lugar.
            </span>
          </div>
        </div>

        {loading && <YoutubeVideoSkeleton />}
        {data && data.youtubePlaylistById && data.youtubePlaylistById.length > 0 && (
          <div className="col-span-4 aspect-video w-full overflow-hidden rounded-2xl shadow-lg lg:col-span-3 xl:col-span-8 xl:w-11/12">
            <iframe
              title={data.youtubePlaylistById[0].video.title}
              className="h-full w-full"
              allowFullScreen={true}
              src={getYoutubeEmbedUrl(data.youtubePlaylistById[0].video.id)}
              frameBorder="0"
            />
          </div>
        )}

        {loading && <PlaylistCardSkeleton />}
        {data && data.youtubePlaylistById && data.youtubePlaylistById.length > 0 && (
          <div className="col-span-4 h-max w-full rounded-xl border border-neutral-200 bg-white py-5 px-4 shadow lg:col-span-1 xl:col-span-3">
            <div className="mb-5 flex items-center space-x-3">
              <div className="h-9 w-9 overflow-hidden rounded-full bg-neutral-200 ring-2 ring-red-500 ring-offset-2">
                <img
                  src={`${getBaseUrl()}/static/people/arthur.png`}
                  alt="Arthur “Asvid” Svidzinski"
                  className="aspect-square object-cover"
                />
              </div>
              <div className="flex flex-col space-y-0.5 font-aeonik">
                <span className="text-sm font-bold text-gray-900">Arthur “Asvid” Svidzinski</span>
                <span className="text-xs font-medium text-gray-500">CNPI nº 2411</span>
              </div>
            </div>

            <div className="flex w-full flex-col space-y-3">
              <hr className="w-full border-t border-t-neutral-100" />
              <div className="flex items-center space-x-3 font-aeonik text-base font-medium text-neutral-600">
                <PlayIcon className="h-5 w-5 fill-neutral-100 text-gray-500" />
                <span>{data.youtubePlaylistById.length} aulas</span>
              </div>

              <hr className="w-full border-t border-t-neutral-100" />
              <div className="flex items-center space-x-3 font-aeonik text-base font-medium text-neutral-600">
                <ClockIcon className="h-5 w-5 fill-neutral-100 text-gray-500" />
                <span>{formatTimeFromSeconds(totalOfDurationInSeconds ?? 0, "long")} de conteúdo</span>
              </div>

              <hr className="w-full border-t border-t-neutral-100" />
              <div className="flex items-center space-x-3 font-aeonik text-base font-medium text-neutral-600">
                <StarIcon className="h-5 w-5 fill-neutral-100 text-gray-500" />
                <span>Gratuito</span>
              </div>
            </div>

            <Link
              href="/irpf/[videoId]"
              as={getVideoHref(data.youtubePlaylistById[0])}
              replace={true}
              passHref={true}
              className={classNames(
                "mt-6 flex h-12 w-full items-center justify-center rounded-lg bg-brand hover:bg-brand/90",
                "font-aeonik text-sm font-bold text-white",
              )}
            >
              Ver todos os vídeos
            </Link>
          </div>
        )}

        <div className="col-span-4 flex flex-col space-y-6 md:col-span-3 xl:col-span-7">
          <div className="flex flex-col items-center font-aeonik md:items-start">
            <h2 className="m-0 text-xl font-bold text-gray-700 xl:text-2xl">Dúvidas que todo mundo tem</h2>
            <span className="text-xs font-normal text-slate-500 xl:text-base">
              As principais dúvidas já respondidas aqui para você ;)
            </span>
          </div>

          <FrequentlyAskedQuestions questions={FREQUENTLY_ASKED_QUESTIONS} />
        </div>

        <div className="col-span-4 flex flex-col space-y-5 xl:col-span-3 xl:col-start-9">
          {dataPostsMostReplied && dataPostsMostReplied.mostRepliedPosts.length > 0 && (
            <>
              <div className="flex flex-col font-aeonik">
                <h2 className="m-0 text-xl font-bold text-gray-700 xl:text-2xl">Fórum IRPF {year}</h2>
                <span className="text-xs font-normal text-slate-500 xl:text-base">Tópicos sugeridos</span>
              </div>

              <div className="flex flex-col space-y-2">
                {dataPostsMostReplied?.mostRepliedPosts.map((post) => (
                  <Link
                    key={post.shortId}
                    href={`/irpf/forum/${post.shortId}`}
                    className="text-ellipsis whitespace-nowrap text-justify font-aeonik text-xs font-normal text-gray-600 underline xl:text-base"
                  >
                    {post.title}
                  </Link>
                ))}
              </div>

              <Link
                href="/irpf/forum"
                className="texr-gray-700 flex items-center space-x-1 text-xs font-normal hover:underline xl:text-base"
              >
                <span>Ver todos tópicos</span>
                <ArrowLongRightIcon className="h-6 w-6" />
              </Link>
            </>
          )}

          {!dataPostsMostReplied ||
            (dataPostsMostReplied && dataPostsMostReplied.mostRepliedPosts.length === 0 && (
              <Link
                href="/irpf/forum"
                className="texr-gray-700 flex items-center space-x-1 text-xs font-normal hover:underline xl:text-base"
              >
                <span>Ir para fórum</span>
                <ArrowLongRightIcon className="h-6 w-6" />
              </Link>
            ))}

          {loadingPostsMostReplied && <PostsMostRepliedSkeleton />}
        </div>
      </div>

      {data && data.youtubePlaylistById && data.youtubePlaylistById.length > 0 && (
        <script
          {...jsonLdScriptProps<VideoObject>({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: data.youtubePlaylistById[0].video.title,
            description: data.youtubePlaylistById[0].video.title,
            thumbnailUrl: [data.youtubePlaylistById[0].video.thumbnails.default.url],
            embedUrl: getYoutubeEmbedUrl(data.youtubePlaylistById[0].video.id),
            uploadDate: data.youtubePlaylistById[0].video.publishedAt,
          })}
        />
      )}
    </NavbarLayout>
  );
}

type TYouTubePlaylistItem = NonNullable<IYoutubePlaylistByIdQuery["youtubePlaylistById"]>[0];

export function getVideoHref(
  video: TYouTubePlaylistItem,
  chapter?: NonNullable<TYouTubePlaylistItem["video"]["chapters"]>[0],
) {
  if (chapter) {
    return `/irpf/${video.video.id}?chapter=${chapter.slug}`;
  }

  return `/irpf/${video.video.id}`;
}

function PostsMostRepliedSkeleton() {
  return (
    <div className="flex animate-pulse flex-col space-y-6">
      <div className="flex flex-col space-y-1">
        <div className="h-8 w-48 rounded-lg bg-slate-200" />
        <div className="h-6 w-32 rounded-lg bg-slate-200" />
      </div>

      <div className="mt-6 flex flex-col space-y-2">
        {Array.from({ length: 5 }).map((index) => {
          // @ts-ignore
          return <div key={index} className="h-6 w-60 rounded-lg bg-slate-200" />;
        })}
      </div>

      <div className="h-4 w-40 rounded-lg bg-slate-200" />
    </div>
  );
}

function YoutubeVideoSkeleton() {
  return (
    <div className="col-span-4 aspect-video w-full animate-pulse overflow-hidden rounded-2xl border bg-slate-100 shadow-lg lg:col-span-3 xl:col-span-8 xl:w-11/12" />
  );
}

function PlaylistCardSkeleton() {
  return (
    <div className="col-span-4 h-max w-full animate-pulse rounded-xl border border-neutral-200 bg-slate-50 py-5 px-4 shadow lg:col-span-1 xl:col-span-3">
      <div className="mb-5 flex items-center space-x-3">
        <div className="h-9 w-9 rounded-full bg-slate-100 ring-2 ring-slate-300 ring-offset-1" />
        <div className="flex w-40 flex-col space-y-0.5">
          <div className="flex h-3 w-full rounded bg-slate-200" />
          <div className="flex h-3 w-full rounded bg-slate-200" />
        </div>
      </div>

      <div className="flex w-full flex-col space-y-3">
        <hr className="w-full border-t border-t-neutral-100" />
        <div className="flex h-5 w-2/3 rounded bg-slate-200" />

        <hr className="w-full border-t border-t-neutral-100" />
        <div className="flex h-5 w-2/3 rounded bg-slate-200" />

        <hr className="w-full border-t border-t-neutral-100" />
        <div className="flex h-5 w-2/3 rounded bg-slate-200" />
      </div>

      <div className="mt-6 h-12 w-full rounded-lg bg-slate-200" />
    </div>
  );
}

const FREQUENTLY_ASKED_QUESTIONS = [
  {
    title: "Qual o objetivo dessa série de vídeos e quais investimentos ela aborda?",
    content: (
      <>
        <p>
          O objetivo principal é ajudar investidores que fazem por conta própria sua declaração anual de Imposto de
          Renda e que não contam com o apoio de um contador ou de alguma ferramenta de cálculo automático.
        </p>

        <strong>Os investimentos abordados são:</strong>
        <ul>
          <li>Ações brasileiras</li>
          <li>Ações no exterior</li>
          <li>Fundos Imobiliários</li>
          <li>ETFs</li>
          <li>BDRs</li>
        </ul>
      </>
    ),
  },
  {
    title: "Quem é obrigado a declarar Imposto de Renda em 2023?",
    content: (
      <ul>
        <li>
          Quem recebeu rendimentos tributáveis acima de <strong className="font-bold">R$28.559,70</strong> no ano, cerca
          de <strong className="font-bold">R$2.380</strong> por mês, incluindo salários, aposentadorias, pensões e
          aluguéis;
        </li>

        <li>
          Quem recebeu rendimento isento, não tributável ou tributado exclusivamente na fonte acima de{" "}
          <strong className="font-bold">R$40 mil</strong>, isso inclui o FGTS, seguro-desemprego, doações, heranças e
          PLR;
        </li>

        <li>Quem teve ganho de capital vendendo bens ou direitos sujeitos a pagamento do IR;</li>

        <li>
          Quem realizou operações na Bolsa de Valores e vendeu acima de <strong className="font-bold">R$40 mil</strong>{" "}
          ou teve ganho de capital acima do limite de isenção de <strong className="font-bold">R$20 mil</strong>;
        </li>

        <li>
          Quem tinha bens ou direitos acima de <strong className="font-bold">R$300</strong> mil em 31/12/2022;
        </li>

        <li>
          Quem teve receita de atividade rural acima de <strong className="font-bold">R$142.798,50</strong>.
        </li>
      </ul>
    ),
  },
  {
    title: "Qual a data limite para enviar a sua declaração em 2023?",
    content: (
      <p>
        O prazo de entrega da declaração vai até o dia <strong>31/05/2023</strong>.
      </p>
    ),
  },
  {
    title: "Quais foram as principais mudanças em 2023 para quem investe na Bolsa de Valores?",
    content: (
      <ul>
        <li>
          <strong>Como era:</strong> até o ano passado, qualquer pessoa que comprasse ou vendesse ações estava obrigada
          a informar a movimentação à Receita, independentemente do valor da operação.
        </li>

        <li>
          <strong>Como ficou:</strong> a partir de agora, só quem fez vendas superiores a <strong>R$40 mil</strong> ao
          longo de 2022 deverá informar.
        </li>

        <li>
          <strong>Limite de ganhos:</strong> quem tiver obtido ganhos líquidos acima de <strong>R$20 mil</strong> também
          precisa declarar.
        </li>

        <li>
          <strong>Day Trade:</strong> Operações de compra e venda de ações no mesmo dia também devem ser informadas na
          declaração de ajuste anual.
        </li>
      </ul>
    ),
  },
  {
    title: "Como evitar cair na Malha Fina?",
    content: (
      <>
        <p>
          Segundo as estatísticas de retenção de declaração em malha dos últimos anos divulgados pela Receita Federal e
          tomando como base as declarações que ficaram retidas na malha fina em 2022 (1.032.279 no total), é possível
          detectar que os erros mais frequentes são:
        </p>

        <ul>
          <li>
            <strong>42%</strong> - Omissão de rendimentos do titular ou de dependentes;
          </li>
          <li>
            <strong>29%</strong> - Informações divergentes das informadas pela fonte pagadora de rendimentos;
          </li>
          <li>
            <strong>22%</strong> - Valores incompatíveis lançados como despesas com saúde;
          </li>
          <li>
            <strong>8%</strong> - Dedução indevida de previdência privada, previdência social ou pensão alimentícia.
          </li>
        </ul>

        <p>Outros erros comuns são:</p>

        <ul>
          <li>Problemas de digitação</li>
          <li>Omissão de rendimentos com aluguel</li>
          <li>Rendimento na ficha errada</li>
          <li>Confundir dependente e alimentando</li>
          <li>Dedução indevida de despesas médicas</li>
          <li>Gastos com educação</li>
          <li>Confundir PGBL com VGBL</li>
          <li>Variação patrimonial incompatível com a renda</li>
          <li>Não declarar o custo de aquisição da ação</li>
          <li>Deixar de conferir dados da pré-preenchida</li>
        </ul>

        <p>
          É recomendável que quem estiver obrigado a fazer a declaração do Imposto de Renda por qualquer outro motivo,
          como rendimento tributável acima de <strong>R$28.559,70</strong>, bens acima de <strong>R$300 mil</strong>,
          entre outros, informe a compra e venda de ações, mesmo abaixo dos limites previstos, caso não informe, pode
          cair na malha fina.
        </p>
      </>
    ),
  },
  {
    title: "Como fazer a declaração pré-preenchida e diminuir seu risco de cair na Malha Fina?",
    content: (
      <>
        <p>
          Na declaração pré-preenchida, o sistema da Receita traz automaticamente diversas informações que antes
          precisavam ser digitadas uma a uma pelo declarante.
        </p>

        <p>
          Ela contém informações relativas a rendimentos, deduções, bens e direitos e dívidas e ônus reais alimentadas
          diretamente no programa do IRPF 2023.
        </p>

        <p>
          O contribuinte fica responsável por verificar todos os dados pré-preenchidos na declaração e deve realizar as
          alterações, inclusões e exclusões das informações, se for o caso.
        </p>

        <p>
          Para poder fazer a declaração pré-preenchida, o contribuinte deve ter uma conta Gov.br de nível prata ou ouro,
          sendo o primeiro nível de autenticação liberado pelo sistema é o bronze.
        </p>

        <p>
          Para conseguir aumentar o nível para prata ou ouro, será necessário fazer o reconhecimento facial pelo celular
          ou tablet, ou acessar o Gov.br com validação de dados bancários de um dos bancos credenciados.
        </p>
      </>
    ),
  },
  {
    title: "Como verificar se você caiu na Malha Fina?",
    content: (
      <>
        <p>
          {`Para saber se a sua declaração caiu na malha fina, acesse o portal e-CAC e selecione a opção "Meu Imposto de
          Renda (Extrato da DIRPF)".`}
        </p>

        <p>
          {`Na aba "Processamento", escolha o item "Pendências de Malha", lá você pode ver se sua declaração está na malha
          fina e também verificar qual é o motivo pelo qual ela foi retida.`}
        </p>

        <p>
          Se a declaração está em malha fina porque você cometeu algum erro no preenchimento ou deixou de informar
          alguma coisa, você pode fazer uma retificação da sua declaração, desde que ainda não tenha recebido o termo de
          intimação enviado pela Receita Federal.
        </p>
      </>
    ),
  },
  {
    title: "O que acontece se você não declarar?",
    content: (
      <>
        <p>
          Para pessoas que não têm obrigação de declarar o imposto, conforme os critérios da Receita Federal, não há
          implicações caso não seja declarado.
        </p>
        <p>
          Entretanto, se você é obrigado a declarar e deixou de cumprir com a obrigação, além da multa, poderá ter seu
          CPF bloqueado, será impossibilitado de renovar passaporte, prestar concursos públicos e fazer empréstimos.
        </p>
      </>
    ),
  },
];
